import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { assignPayment, duplicateReview } from '@/api/desk';
import { reconcileWalletPayments, reversePayment } from '@/api/bursar';
import { useNavigate } from 'react-router-dom';
import { DataTable, DateInput, FormModal, KV, Modal, Notice, Pager, Search, StudentPicker, dt, errMsg, money, newKey, useAction, useBase, useGet, useList } from '@/features/shared/ops/kit';
import { PaymentReceipt } from './ReceiptsPage';

function PaymentDetail({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const q = useGet<any>('payment', `/school/payments/${id}`);
  const a = useAction(() => { qc.invalidateQueries({ queryKey: ['payment'] }); onChanged(); });
  const [dlg, setDlg] = useState<null | 'reverse' | 'assign' | 'dup' | 'receipt'>(null);
  const [student, setStudent] = useState<any>(null);
  const p = q.data;
  const canReverse = p && p.status === 'successful';
  return (
    <Modal title={p ? `Payment ${p.reference}` : 'Payment'} onClose={onClose} wide>
      {q.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {q.error && <Notice kind="error">{errMsg(q.error)}</Notice>}
      {a.banner}
      {p && (
        <div className="space-y-3">
          {p.unmatched && <Notice>This payment is not linked to a student yet. Assign it to issue a receipt.</Notice>}
          {p.possible_duplicate_of && <Notice>Possible duplicate of payment #{p.possible_duplicate_of}.</Notice>}
          {p.refund_due && <Notice kind="error">This payment was reversed and the money still has to be refunded to the payer.</Notice>}
          <KV k="Status" v={<StatusBadge status={p.status} />} />
          <KV k="Amount" v={money(p.amount)} />
          <KV k="Fee" v={money(p.fee)} />
          <KV k="Method" v={`${p.method ?? ''}${p.channel ? ' · ' + p.channel : ''}`} />
          <KV k="Student" v={p.student ? `${p.student} (${p.admission_no})` : 'Unassigned'} />
          <KV k="Payer" v={p.payer_name ?? '—'} />
          <KV k="Paid at" v={dt(p.paid_at)} />
          <KV k="Receipt" v={p.receipt_no ?? '—'} />
          <KV k="Unallocated" v={money(p.unallocated)} />
          {p.narration && <KV k="Narration" v={p.narration} />}
          <DataTable rows={p.allocations ?? []} empty="Not allocated to any invoice." cols={[{ head: 'Invoice', cell: (r: any) => r.invoice_no }, { head: 'Amount', right: true, cell: (r: any) => money(r.amount) }]} />
          <div className="flex gap-2 flex-wrap">
            {p.receipt_id && <Button variant="ghost" onClick={() => setDlg('receipt')}>View receipt</Button>}
            {p.unmatched && <Button onClick={() => setDlg('assign')}>Assign to student</Button>}
            {p.possible_duplicate_of && p.status === 'successful' && <Button variant="ghost" onClick={() => setDlg('dup')}>Review duplicate</Button>}
            {canReverse && <Button variant="danger" onClick={() => setDlg('reverse')}>Reverse</Button>}
          </div>
        </div>
      )}
      {dlg === 'receipt' && <PaymentReceipt paymentId={id} onClose={() => setDlg(null)} />}
      {dlg === 'reverse' && (
        <FormModal title="Reverse payment" fields={[{ name: 'reason', label: 'Reason', required: true, maxLength: 255 }]} submitLabel="Reverse" onClose={() => setDlg(null)} onSubmit={async (v) => { await reversePayment(id, v.reason, newKey()); qc.invalidateQueries({ queryKey: ['payment'] }); onChanged(); }} />
      )}
      {dlg === 'dup' && (
        <FormModal
          title="Duplicate review"
          fields={[
            { name: 'action', label: 'Decision', type: 'select', required: true, options: [{ value: 'keep', label: 'Keep: it is a genuine second payment' }, { value: 'reverse', label: 'Reverse the duplicate' }] },
            { name: 'reason', label: 'Reason', maxLength: 255, show: (v) => v.action === 'reverse', required: true },
          ]}
          onClose={() => setDlg(null)}
          onSubmit={async (v) => { await duplicateReview(id, v, newKey()); qc.invalidateQueries({ queryKey: ['payment'] }); onChanged(); }}
        />
      )}
      {dlg === 'assign' && (
        <Modal title="Assign to student" onClose={() => setDlg(null)}>
          <StudentPicker value={student} onPick={setStudent} />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setDlg(null)}>Cancel</Button>
            <Button disabled={!student || a.busy} onClick={() => { a.run(() => assignPayment(id, { student_id: student.id }, newKey())); setDlg(null); }}>Assign</Button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

const METHOD: Record<string, string> = { cash: 'Cash', pos: 'POS', bank_deposit: 'Bank deposit', cheque: 'Cheque', transfer: 'Transfer', card: 'Card', wallet: 'Wallet' };
const chip = (on: boolean) => `px-3 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap ${on ? 'bg-[#0A2540] text-white border-[#0A2540]' : 'bg-white border-slate-300 text-slate-700'}`;

export function PaymentsPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const base = useBase();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [method, setMethod] = useState('');
  const [flag, setFlag] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const list = useList('payments', '/school/payments', { q, status, method, flag, from, to });
  const refresh = () => qc.invalidateQueries({ queryKey: ['payments'] });
  const a = useAction(refresh);
  const ok = list.rows.filter((r: any) => r.status === 'successful');
  const total = ok.reduce((s: number, r: any) => s + Number(r.amount ?? 0), 0);
  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Collections</p><h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Payments</h1>
          <p className="text-sm text-slate-500">Every fee payment: online transfers, wallet, cash, POS, bank deposits and cheques. Tap one for its receipt.</p></div>
        <div className="flex gap-2 shrink-0">
          <button disabled={a.busy} onClick={() => a.run(() => reconcileWalletPayments(newKey()), { confirm: 'Check the wallet provider for payments we may have missed?' })} className="px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm font-bold">Find missing</button>
          <button onClick={() => nav(`${base}/record-payment`)} className="px-4 py-2.5 rounded-xl bg-[#0A2540] text-white text-sm font-bold">Collect a payment</button>
        </div>
      </div>
      {a.banner}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Received (this page)</p><p className="text-2xl font-extrabold text-green-700">{money(total)}</p><p className="text-[11px] text-slate-400">{ok.length} payment(s)</p></div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4"><p className="text-xs text-slate-500">All matching</p><p className="text-2xl font-extrabold text-slate-900">{list.meta?.total ?? list.rows.length}</p></div>
        <button onClick={() => nav(`${base}/reconciliation`)} className="text-left bg-white rounded-2xl border border-slate-200 p-4 col-span-2 sm:col-span-1"><p className="text-xs text-slate-500">Need a decision?</p><p className="text-sm font-semibold text-[#0A2540] mt-1">Unmatched, duplicates and refunds → Reconciliation</p></button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-3 space-y-2.5">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[180px]"><Search value={q} onChange={setQ} placeholder="Reference, payer or student" /></div>
          <DateInput label="From" value={from} onChange={setFrom} />
          <DateInput label="To" value={to} onChange={setTo} />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          <button onClick={() => setMethod('')} className={chip(!method)}>All methods</button>
          {Object.entries(METHOD).map(([k, l]) => <button key={k} onClick={() => setMethod(method === k ? '' : k)} className={chip(method === k)}>{l}</button>)}
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {[['', 'Any status'], ['successful', 'Successful'], ['reversed', 'Reversed'], ['failed', 'Failed']].map(([k, l]) => <button key={l} onClick={() => setStatus(k)} className={chip(status === k)}>{l}</button>)}
          <span className="w-px bg-slate-200 mx-1" />
          {[['unmatched', 'Unmatched'], ['duplicate', 'Duplicates?'], ['refund_due', 'Refund due']].map(([k, l]) => <button key={k} onClick={() => setFlag(flag === k ? '' : k)} className={chip(flag === k)}>{l}</button>)}
        </div>
      </div>
      <section className="bg-white rounded-2xl border border-slate-200">
        {list.isLoading ? <p className="p-5 text-sm text-slate-500">Loading…</p> : list.error ? <p className="p-5 text-sm text-red-600">{errMsg(list.error)}</p> : !list.rows.length ? <p className="p-8 text-center text-sm text-slate-500">No payments match.</p> : (
          <ul className="divide-y divide-slate-100">
            {list.rows.map((r: any) => (
              <li key={r.id}><button onClick={() => setOpen(r.id)} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
                <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 text-xs font-bold ${r.status === 'successful' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>{(METHOD[r.method] ?? r.method ?? '?').slice(0, 3)}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-slate-900 truncate">{r.student ? r.student : r.payer_name ?? 'Not linked to a student'}{r.admission_no ? <span className="font-normal text-slate-500"> · {r.admission_no}</span> : null}</span>
                  <span className="block text-xs text-slate-500 truncate">{METHOD[r.method] ?? r.method} · {dt(r.paid_at)} · <span className="font-mono">{r.reference}</span></span>
                </span>
                <span className="text-right shrink-0">
                  <span className={`block font-extrabold ${r.status === 'successful' ? 'text-slate-900' : 'text-slate-400 line-through'}`}>{money(r.amount)}</span>
                  <span className="block text-[11px]">{r.unmatched ? <span className="text-amber-700 font-semibold">Assign</span> : r.possible_duplicate_of ? <span className="text-red-600 font-semibold">Duplicate?</span> : <StatusBadge status={r.status} />}</span>
                </span>
              </button></li>
            ))}
          </ul>
        )}
        <div className="px-4 pb-3"><Pager meta={list.meta} page={list.page} onPage={list.setPage} /></div>
      </section>
      {open != null && <PaymentDetail id={open} onClose={() => setOpen(null)} onChanged={refresh} />}
    </div>
  );
}
